import { validateEventProperties } from "./events";
import { dispatchPage, dispatchTrack, initAdapters } from "./adapters";
import {
  getAnonymousId,
  getCurrentPageType,
  getLanguage,
  resolvePageType,
  setCurrentPageType,
} from "./context";
import {
  closeCheckoutAttemptIfLeaving,
  markCheckoutStarted,
  getCheckoutAttemptId,
} from "./checkoutAttempt";
import { fetchGaClientAndSessionIds } from "./adapters/gtag";
import { seatFiltersForGame } from "./seatFilters";

const pageDedupeKeys = new Set();
let lastPageViewPath = null;

export function resetPageDedupe() {
  pageDedupeKeys.clear();
}

export function resetPageViewForTests() {
  lastPageViewPath = null;
}

/** Fire at most once per key until the next analytics.page(). */
export function trackOncePerPage(dedupeKey, eventName, properties) {
  if (pageDedupeKeys.has(dedupeKey)) {
    return;
  }
  pageDedupeKeys.add(dedupeKey);
  track(eventName, properties);
}

export function init() {
  try {
    initAdapters();
  } catch {
    // never block startup
  }
}

export function track(eventName, properties) {
  try {
    if (!validateEventProperties(eventName, properties)) {
      return;
    }
    dispatchTrack(eventName, properties, {
      language: getLanguage(),
      pageType: getCurrentPageType(),
      anonymousId: getAnonymousId(),
    });
  } catch {
    // never block checkout
  }
}

export function page(pathname) {
  try {
    const path = pathname || "/";
    const pageType = resolvePageType(path);
    setCurrentPageType(pageType);
    closeCheckoutAttemptIfLeaving(pageType);
    resetPageDedupe();
    if (path === lastPageViewPath) {
      return;
    }
    lastPageViewPath = path;
    dispatchPage({
      path,
      pageType,
      language: getLanguage(),
      anonymousId: getAnonymousId(),
    });
  } catch {
    // ignore
  }
}

export function identify() {
  try {
    getAnonymousId();
  } catch {
    // ignore
  }
}

export function checkoutFailureContext(seat, quantity) {
  const chosen = Number(quantity);
  const listed = seat ? Number(seat.noOfSeats) : NaN;
  const resolved =
    Number.isFinite(chosen) && chosen > 0
      ? chosen
      : Number.isFinite(listed) && listed > 0
        ? listed
        : 1;
  return {
    provider: listingProvider(seat),
    quantity: resolved,
  };
}

export function listingProvider(seat) {
  if (!seat) {
    return "fansfirst";
  }
  if (seat.tnListingId || (seat.tnDetails && seat.tnDetails !== null)) {
    return "tn";
  }
  if (seat.teListingId || seat.teDetails) {
    return "te";
  }
  return "fansfirst";
}

export function priceBand(price) {
  const p = Number(price);
  if (!Number.isFinite(p)) {
    return "under_100";
  }
  if (p < 100) {
    return "under_100";
  }
  if (p < 250) {
    return "100_249";
  }
  if (p < 500) {
    return "250_499";
  }
  return "500_plus";
}

export function gameClickTarget(zone) {
  const map = {
    0: "date_opponent",
    1: "press_level",
    2: "second_level",
    3: "lower_bowl",
  };
  return map[zone] || "date_opponent";
}

export function parseGridDisplayPrice(raw) {
  if (raw == null || raw === "" || raw === "-") {
    return undefined;
  }
  const cleaned = String(raw).replace(/\s*\([^)]*\)/g, "").replace(/[^0-9.]/g, "");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

export function paymentMethodFromOptionId(optionId) {
  if (optionId === 2) {
    return "apple_pay";
  }
  if (optionId === 3) {
    return "google_pay";
  }
  return "card";
}

export async function analyticsIdsForCheckout() {
  let gaIds = {};
  try {
    gaIds = await fetchGaClientAndSessionIds();
  } catch {
    gaIds = {};
  }
  const anonymousId = getAnonymousId();
  return {
    ...gaIds,
    ...(anonymousId ? { anonymous_id: anonymousId } : {}),
  };
}

export function paymentFailureFromResponse(data, status) {
  const body = data && typeof data === "object" ? data : {};
  if (
    status === 409 ||
    body.isSeatAvailable === false ||
    body.messageCode === "TE_LISTING_UNAVAILABLE" ||
    body.messageCode === "SEAT_UNAVAILABLE" ||
    body.messageCode === "PRICE_CHANGED"
  ) {
    return { code: "unavailable", step: "payment" };
  }
  if (body.messageCode === "PROVIDER_BUSY_RETRY" || status === 503) {
    return { code: "busy", step: "payment" };
  }
  if (
    body.cardError ||
    body.messageCode === "CARD_DECLINED" ||
    body.messageCode === "REVERSED" ||
    body.messageCode === "INVALID_EXPIRY" ||
    body.messageCode === "AVS_MISMATCH"
  ) {
    return { code: "declined", step: "payment" };
  }
  if (
    body.invalidNumber ||
    body.invalidCoupon ||
    body.messageCode === "INVALID_PHONE" ||
    body.messageCode === "MISSING_EMAIL" ||
    body.messageCode === "INVALID_EMAIL" ||
    body.messageCode === "MISSING_NAME" ||
    body.messageCode === "MISSING_PHONE"
  ) {
    return { code: "invalid", step: "payment" };
  }
  if (body.isBlocked || body.isVPN || body.messageCode === "BLOCKED_ORDER") {
    return { code: "blocked", step: "payment" };
  }
  return { code: "error", step: "payment" };
}

export function tryTrackCheckoutStarted(properties) {
  try {
    const listingId = properties && properties.listing_id;
    const attemptId = markCheckoutStarted(listingId);
    if (!attemptId) {
      return;
    }
    const activeFilters =
      properties.active_filters || seatFiltersForGame(properties.game_id);
    track("checkout_started", {
      ...properties,
      ...(activeFilters ? { active_filters: activeFilters } : {}),
      checkout_attempt_id: attemptId,
      currency: properties.currency || "CAD",
    });
  } catch {
    // ignore
  }
}

export { markCheckoutStarted, getCheckoutAttemptId } from "./checkoutAttempt";
export { fetchGaClientAndSessionIds };
export { GA4_EVENT_MAP } from "./ga4";
export { CANONICAL_EVENTS } from "./events";

const analyticsApi = {
  init,
  track,
  page,
  identify,
  trackOncePerPage,
  tryTrackCheckoutStarted,
  markCheckoutStarted,
  getCheckoutAttemptId,
  fetchGaClientAndSessionIds,
};

export default analyticsApi;
